import type { QueryClient } from '@tanstack/react-query'

import { ensureRouteData } from '@/app/query-client'
import { liveMarketQueryOptions, priceHistoryQueryOptions } from '@/features/prices/api/queries'
import {
  fetchRangeOf,
  periodOf,
  toTradingSearch,
  type TradingSearch,
} from '@/features/trading-history/lib/tradingSearch'
import { liveMarketQuerySchema } from '@/schemas/prices'

/** The day's busiest name, as Live Market ranks it: the company a bare URL opens on. */
const BUSIEST = liveMarketQuerySchema.parse({ sortBy: 'volume', sortDesc: true, pageSize: 10 })

/**
 * The most-traded company today, or `undefined` when Live Market cannot say.
 * A failure here is not this page's failure — the page still opens, on its
 * company chooser — so it is answered with `undefined`, not thrown.
 */
async function busiestSymbol(client: QueryClient): Promise<string | undefined> {
  try {
    const page = await ensureRouteData(client, liveMarketQueryOptions(BUSIEST))
    return page.rows[0]?.symbol
  } catch {
    return undefined
  }
}

/**
 * What both trading routes' loaders do first: read the company's sessions for
 * the entry URL's period, awaited so the figures land on the first frame.
 *
 * A URL with no company resolves to the busiest one and reports it as
 * `redirect`, for the route's own loader to throw (a `redirect`'s `to` is typed
 * per route). The address then always names what is on screen, and a shared
 * link opens the same page.
 *
 * The sessions read may fail without failing the route: a mistyped symbol or a
 * dropped connection is answered in the page (its own failed state, with the
 * company chooser still on it), not by a whole-page error with nothing to pick
 * from. Read from `location.search`, not `loaderDeps`: a company or period
 * pick then re-runs no loader and the page dims its own rows instead of
 * falling back to the route's skeleton.
 */
export async function warmTradingPage({
  context: { queryClient },
  location,
}: {
  context: { queryClient: QueryClient }
  location: { search: unknown }
}): Promise<{ search: TradingSearch; redirect: boolean }> {
  const entry = toTradingSearch(location.search)
  const isBare = entry.symbol === undefined
  const symbol = entry.symbol ?? (await busiestSymbol(queryClient))
  const search = { ...entry, symbol }
  if (symbol !== undefined) {
    try {
      await ensureRouteData(
        queryClient,
        priceHistoryQueryOptions(symbol, fetchRangeOf(periodOf(search))),
      )
    } catch {
      // See above: the page says so.
    }
  }
  return { search, redirect: isBare && symbol !== undefined }
}
